import React, { useEffect, Suspense, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Provider } from "react-redux";
import store from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// FB-P1': firebase/app is no longer imported eagerly here — that (together with
// the eager PaymentSuccess import below) pulled the whole Firebase vendor chunk
// (~73KB br, incl. firestore/storage) into the initial cold-boot graph for every
// visitor. initializeApp is now imported dynamically inside the messaging effect,
// behind a getApps()/getApp() guard.
import { lazyWithRetry, CHUNK_RELOAD_KEY } from "./websiteComponent/commonComponent/lazyWithRetry";
import RouteErrorBoundary from "./websiteComponent/commonComponent/RouteErrorBoundary";
// PD-P1: eager (NOT lazy) so it can render as the Product Details route's
// Suspense fallback WHILE the PriceDetails chunk downloads — replacing the
// previous blank fallback for that route only.
import PdSkeleton from "./websiteComponent/offersComponent/detailComponents/pdSkeleton";
import OfflineOverlay from "./websiteComponent/commonComponent/OfflineOverlay";
import VersionGuard from "./websiteComponent/commonComponent/VersionGuard";
// WEB-PHASE-1A-SOCKET (shadow): observe-only Socket.IO mount. No-op unless
// REACT_APP_SOCKET_REFRESH=shadow. Firebase listeners stay authoritative.
import SocketProvider from "./socket/SocketProvider";
// import {
//   getMessaging,
//   getToken,
//   onMessage,
//   isSupported,
// } from "firebase/messaging";
import {
  getFCMToken,
  readNotification,
  updateFCMToken,
} from "./websiteComponent/apiServices/home/homeHttpService";
import { TranslationProvider } from "./websiteComponent/commonComponent/TranslationContext";
import ProtectedRoute from "./websiteComponent/commonComponent/protectedRoutes";
import { useGlobalAlert } from "./websiteComponent/commonComponent/useGlobalAlert";
// FB-P1': lazy (was an eager static import). PaymentSuccess pulls in firebase.js
// (firestore + storage), so importing it eagerly kept Firebase in the initial
// graph. As a lazy route it now loads only when /payment-status is visited, and
// it is covered by the existing top-level <Suspense> like every other route.
const PaymentSuccess = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/paymentSuccess")
);
const SupportPaymentSuccess = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/supportPayment")
);
const TransactionDetails = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/transactionDetails")
);
const SupportDepositDetails = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/supportDepositDetails")
);
const Home = lazyWithRetry(() => import("./websiteComponent/home"));
const Deposit = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/deposit")
);
const Transfer = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/transfer")
);

const BankDetails = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/bankDetails")
);
const QrCodePayment = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/qrCodePayment")
);
const SellerQrCode = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/sellerQrCode")
);
const FeeCalculator = lazyWithRetry(
  () => import("./websiteComponent/helpComponent/feeCalculator")
);
const BidPayment = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/detailComponents/bidPayment")
);
const CartBuyer = lazyWithRetry(
  () => import("./websiteComponent/cartComponent/cartBuyer")
);
const CartRequest = lazyWithRetry(
  () => import("./websiteComponent/cartComponent/cartRequest")
);
const CartSeller = lazyWithRetry(
  () => import("./websiteComponent/cartComponent/cartSeller")
);
const CreateShipment = lazyWithRetry(
  () => import("./websiteComponent/cartComponent/createShipment")
);
const BidAuctionRequest = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/bitAuctionRequest")
);
const CartAuction = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/cartAuction")
);
const CartBid = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/cartBid")
);
const EditPrice = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/editPrice")
);
const EditAuction = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/editAuction")
);
const EditFree = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/editFree")
);
const CartPurBuyer = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/cartPurBuyer")
);
const CartPurSeller = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/cartPurSeller")
);
const QrCodeScanner = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/qrCodeScanner")
);
const Auction = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/auction")
);
const Free = lazyWithRetry(() => import("./websiteComponent/offersComponent/free"));
const Price = lazyWithRetry(() => import("./websiteComponent/offersComponent/price"));
const Logout = lazyWithRetry(() => import("./websiteComponent/commonComponent/logout"));
const Location = lazyWithRetry(() => import("./websiteComponent/location"));
const Cart = lazyWithRetry(() => import("./websiteComponent/cart"));
const DeliveryProcess = lazyWithRetry(
  () => import("./websiteComponent/deliveryProcess")
);
const MyOrders = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/myOrders")
);
const ContactUs = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/contactUs")
);
const WebOrderDetails = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/webOrderDetails")
);
const SimilarProduct = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/similarProduct")
);
const TrackPackage = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/trackPackage")
);

const MyWallet = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/myWallet")
);
const Notifications = lazyWithRetry(
  () => import("./websiteComponent/servicesComponent/notifications")
);
const SupportUs = lazyWithRetry(
  () => import("./websiteComponent/helpComponent/supportUs")
);
const Settings = lazyWithRetry(
  () => import("./websiteComponent/helpComponent/settings")
);
const Help = lazyWithRetry(() => import("./websiteComponent/helpComponent/help"));

const HelpDetail = lazyWithRetry(
  () => import("./websiteComponent/helpComponent/helpDetail")
);

const Privacy = lazyWithRetry(
  () => import("./websiteComponent/policyComponent/privacy")
);
const Return = lazyWithRetry(() => import("./websiteComponent/policyComponent/return"));
const TermCondition = lazyWithRetry(
  () => import("./websiteComponent/policyComponent/termCondition")
);
const Followers = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/followers")
);
const QrCode = lazyWithRetry(() => import("./websiteComponent/sideBarComponent/qrCode"));
const MyShopCenter = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/myShopCenter")
);
const ShareThoughts = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/shareThoughts")
);
const AnnouncementDetails = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/announcementDetails")
);
const WebAnnouncements = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/webAnnouncements")
);
const BlockedList = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/blockedList")
);
const Favourite = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/favourite")
);
const MyProfile = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/myProfile")
);
const PostedOn = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/postedOn")
);
const PurchaseRequest = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/purchaseRequest")
);
const PriceDetails = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/priceDetails")
);
const PublicProfile = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/publicProfile")
);
const ManageProduct = lazyWithRetry(
  () => import("./websiteComponent/offersComponent/manageProduct")
);

const BidAuction = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/bidAuction")
);
const PublicQrCode = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/publicQrCode")
);
const SelectAddress = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/selectAddress")
);
const ScrollToTop = lazyWithRetry(
  () => import("./websiteComponent/commonComponent/scrollToTop")
);
const UserFollowers = lazyWithRetry(
  () => import("./websiteComponent/sideBarComponent/userFollowers")
);
const NewChat = lazyWithRetry(() => import("./websiteComponent/newChat"));

const config = {
  apiKey: "AIzaSyD0P1XQYUgpT7WNke5F1zXXV-_0USxZHNA",
  authDomain: "offeryard-d3105.firebaseapp.com",
  databaseURL: "https://offeryard-d3105.firebaseio.com",
  projectId: "offeryard-d3105",
  storageBucket: "offeryard-d3105.appspot.com",
  messagingSenderId: "472517866531",
  appId: "1:472517866531:web:664ba6681b2b82bdfc6d96",
  measurementId: "G-ENNV96325X",
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: false,
      keepPreviousData: true,
      // One automatic retry, but never for auth or 4xx — those won't fix
      // themselves and a retry just delays the page-level error UI.
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        const kind = error && error.kind;
        if (kind === "auth" || kind === "client") return false;
        return true;
      },
      retryDelay: 1500,
      // Don't fire queries while offline; they'll resume on reconnect.
      networkMode: "online",
    },
  },
});

let persistor = persistStore(store);
const currentLangCode = localStorage.getItem("lang");

// PG-P3: lightweight, static, locale-free shell shown by <PersistGate> while
// the (now tiny: phone+location only) redux-persist rehydration completes.
// Intentionally dependency-free — plain inline styles, no imported components,
// no translations, no images — so it adds nothing to the initial cold-boot
// graph. Direction-neutral and full-viewport so it never flashes a jarring
// blank or shifts layout before the router/route skeletons take over.
const PersistLoading = () => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#ffffff",
    }}
  >
    <div
      aria-hidden="true"
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: "3px solid rgba(20,72,129,0.15)",
        borderTopColor: "#FF3F55",
        animation: "oyPersistSpin 0.8s linear infinite",
      }}
    />
    <style>{"@keyframes oyPersistSpin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

function App() {
  const [noti, setNoti] = useState(false);
  useGlobalAlert();

  // Online/offline UX is handled by <OfflineOverlay /> mounted below. The
  // previous implementation gated the entire React tree on `navigator.onLine`
  // and tried to navigate to `chrome-error://chromewebdata/` — a
  // browser-internal URL the page can't actually open. The result was that a
  // brief disconnect (laptop sleep/wake, captive portal flap) tore down
  // Provider/PersistGate/QueryClient/Router and lost the user's place in the
  // app even after the connection came back. The overlay sits on top instead,
  // so routes and data stay alive throughout the offline window.

  // Clear the lazy-import reload sentinel as soon as App successfully
  // mounts. If we got here, the new chunks loaded fine; future chunk
  // failures within this session deserve their own one-shot reload
  // attempt instead of being suppressed by a leftover flag.
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  // NOTE: the app-wide installDigitNormalizer() global input listener was
  // REMOVED here — it rewrote .value on every input event, which fought React's
  // controlled inputs (notably the chat textbox) and broke English/number
  // typing. Per owner decision, any future digit normalization must be applied
  // locally per field, never as a global listener.

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          console.log("Service Worker registered:", registration);
        })
        .catch((err) => {
          console.error("Service Worker registration failed:", err);
        });
    }
  }, []);

  // useEffect(async () => {
  //   if (!localStorage.getItem("lang")) {
  //     localStorage.setItem("lang", "en");
  //   }
  //   setTimeout(() => {
  //     import("firebase/messaging").then(
  //       ({ getMessaging, getToken, onMessage, isSupported }) => {
  //         const app = initializeApp(config);
  //         const messaging = getMessaging(app);
  //         isSupported().then((support) => {
  //           console.log(support);
  //           if (support && Notification.permission === "granted") {
  //             getFirebaseToken(getMessaging(app));
  //           }
  //         });
  //         const getFirebaseToken = () => {
  //           getToken(messaging, {
  //             vapidKey: process.env.REACT_APP_VALIDFCMTOKENKEY,
  //           })
  //             .then((currentToken) => {
  //               if (currentToken) {
  //                 console.log(currentToken);
  //                 localStorage.setItem("deviceId", currentToken);
  //                 updateFCM();
  //               } else {
  //                 console.log(
  //                   "No registration token available. Request permission to generate one."
  //                 );
  //               }
  //             })
  //             .catch((err) => {
  //               console.log("An error occurred while retrieving token. ", err);
  //             });
  //         };
  //         onMessage(messaging, (payload) => {
  //           console.log(payload);
  //           const options = {
  //             body: payload.notification.body,
  //             icon: payload.notification.icon,
  //             image: payload.notification.image,
  //             dir: currentLangCode == "ar" ? "rtl" : "ltr",
  //             vibrate: true,
  //             silent: false,
  //           };
  //           let notification = new Notification(
  //             payload.notification.title,
  //             options
  //           );
  //           if (payload.data.sound) {
  //             const audio = new Audio(payload.data.sound);
  //             audio.play();
  //           }
  //           notification.onclick = function (event) {
  //             event.preventDefault();
  //             const link = document.createElement("a");
  //             link.href = payload.fcmOptions.link;
  //             link.click();

  //             redirect(payload.data.notificationId);
  //           };

  //           localStorage.setItem(
  //             "notii",
  //             localStorage.getItem("notii") === "true" ? "false" : "true"
  //           );
  //           setNoti(!noti);
  //         });
  //       }
  //     );
  //     getFCM();
  //   }, 500);
  //   if (!localStorage.getItem("uid")) {
  //     localStorage.setItem("uid", uuidv4());
  //   }
  // }, []);

  useEffect(() => {
    if (!localStorage.getItem("lang")) {
      localStorage.setItem("lang", "ar");
    }
    const initFirebaseMessaging = async () => {
      try {
        const { getMessaging, getToken, onMessage, isSupported } = await import(
          "firebase/messaging"
        );

        const supported = await isSupported();
        if (!supported) {
          console.log("Firebase messaging not supported");
          return;
        }

        let permission = Notification.permission;
        if (permission === "default") {
          permission = await Notification.requestPermission();
        }

        if (permission !== "granted") {
          console.log("Notification permission not granted");
          return;
        }

        // FB-P1': dynamically import firebase/app (keeps it out of the initial
        // bundle) and guard against duplicate default-app init — firebase.js may
        // have already created [DEFAULT] when a Firestore consumer mounted, or
        // this path may run first. Same config either way.
        const { initializeApp, getApps, getApp } = await import("firebase/app");
        const app = getApps().length ? getApp() : initializeApp(config);
        const messaging = getMessaging(app);

        // 5️⃣ Wait for Service Worker to be ACTIVE
        if (!("serviceWorker" in navigator)) {
          console.log("Service worker not supported");
          return;
        }

        const swRegistration = await navigator.serviceWorker.ready;

        const currentToken = await getToken(messaging, {
          vapidKey:
            "BLFONlYQltSPZijO8FUZeZuk6oUjkqP1DN4pi9Yjes87MuTY6fbFFcjalZ_O8U7AQSXaa5-F1D3EMCMQxgAKKPU",
          // vapidKey: process.env.REACT_APP_VALIDFCMTOKENKEY,
          serviceWorkerRegistration: swRegistration,
        });

        if (currentToken) {
          localStorage.setItem("deviceId", currentToken);
          updateFCM();
        } else {
          console.log("No FCM token generated");
        }

        onMessage(messaging, (payload) => {

          const options = {
            body: payload?.notification?.body,
            icon: payload?.notification?.icon,
            image: payload?.notification?.image,
            dir: currentLangCode === "ar" ? "rtl" : "ltr",
            vibrate: true,
            silent: false,
          };

          const notification = new Notification(
            payload?.notification?.title || "New Notification",
            options
          );

          if (payload?.data?.sound && payload?.data?.sound !== "default") {
            const audio = new Audio(payload.data.sound);
            audio.play().catch((err) => {
              console.error("Error playing notification sound:", err);
            });
          }

          notification.onclick = (event) => {
            event.preventDefault();

            if (payload?.fcmOptions?.link) {
              window.open(payload.fcmOptions.link, "_blank");
            }

            if (payload?.data?.notificationId) {
              redirect(payload.data.notificationId);
            }
          };

          localStorage.setItem(
            "notii",
            localStorage.getItem("notii") === "true" ? "false" : "true"
          );
          setNoti((prev) => !prev);
        });
      } catch (error) {
        console.error("FCM init error:", error);
      }
    };

    initFirebaseMessaging();
    getFCM();
    if (!localStorage.getItem("uid")) {
      localStorage.setItem("uid", uuidv4());
    }
  }, []);
  const redirect = async (id) => {
    const { data } = await readNotification(id);
    if (data.error) {
      if (alert.removeAll) alert.removeAll();
      if (alert.error) alert.error(data.message, { timeout: 2000 });
    }
  };

  const getFCM = async () => {
    if (!localStorage.getItem("token-off")) return;
    const payload = { deviceOS: "Web", deviceId: localStorage.getItem("uid") };
    const { data } = await getFCMToken(payload);
    if (data.error) {
      if (alert.removeAll) alert.removeAll();
      if (alert.error) alert.error(data.message, { timeout: 2000 });
    } else {
      if (data.results.token != localStorage.getItem("deviceId")) updateFCM();
    }
  };

  const updateFCM = async () => {
    if (!localStorage.getItem("token-off")) return;
    const payload = {
      deviceOS: "Web",
      deviceId: localStorage.getItem("uid"),
      fcmToken: localStorage.getItem("deviceId"),
      language: currentLangCode == "ar" ? "Arabic" : "English",
    };
    const { data } = await updateFCMToken(payload);
    if (data.error) {
      if (alert.removeAll) alert.removeAll();
      if (alert.error) alert.error(data.message, { timeout: 2000 });
    }
  };

  const isArabicText = (text = "") => {
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    return arabicRegex.test(text);
  };


  return (
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={<PersistLoading />}>
        <QueryClientProvider client={queryClient}>
          <SocketProvider>
          <BrowserRouter>
            <VersionGuard />
            <OfflineOverlay />
            <Suspense fallback={<div></div>}>
              <ScrollToTop />
              <TranslationProvider>
                <RouteErrorBoundary>
                  <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/location" element={<Location />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/support-us" element={<SupportUs />} />
                  <Route
                    path="/support-payment-status"
                    element={<SupportPaymentSuccess />}
                  />
                  <Route
                    path="/announcements"
                    element={<WebAnnouncements />}
                  />
                  <Route
                    path="/announcement-details/:id"
                    element={<AnnouncementDetails />}
                  />
                  <Route
                    path="/fee-calculator"
                    element={<FeeCalculator />}
                  />
                  <Route path="/help" element={<Help />} />
                  <Route path="/help-detail/:id" element={<HelpDetail />} />
                  <Route path="/privacy-policy" element={<Privacy />} />
                  <Route path="/return-policy" element={<Return />} />
                  <Route
                    path="/term&condition"
                    element={<TermCondition />}
                  />
                  <Route
                    path="/share-your-thoughts"
                    element={<ShareThoughts />}
                  />
                  <Route
                    path="/offer-details/:id"
                    element={
                      <Suspense fallback={<PdSkeleton />}>
                        <PriceDetails />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/public-profile/:id"
                    element={<PublicProfile />}
                  />
                  <Route
                    path="/public-qr-code/:id"
                    element={<PublicQrCode />}
                  />
                  <Route
                    path="/user-followers/:id"
                    element={<UserFollowers />}
                  />

                  <Route element={<ProtectedRoute />}>
                    <Route path="/post-offer-price" element={<Price />} />
                    <Route
                      path="/post-offer-auction"
                      element={<Auction />}
                    />
                    <Route path="/post-offer-free" element={<Free />} />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route
                      path="/delivery-process/:id"
                      element={<DeliveryProcess />}
                    />
                    <Route path="/chat" element={<NewChat />} />
                    <Route path="/activities" element={<MyOrders />} />
                    <Route
                      path="/order-details/:id"
                      element={<WebOrderDetails />}
                    />
                    <Route
                      path="/similar-products/:id"
                      element={<SimilarProduct />}
                    />
                    <Route
                      path="/track-package/:id"
                      element={<TrackPackage />}
                    />

                    <Route path="/my-wallet" element={<MyWallet />} />
                    <Route
                      path="/transation-history-detail/:id"
                      element={<TransactionDetails />}
                    />
                    <Route
                      path="/transation-detail-for-support/:id"
                      element={<SupportDepositDetails />}
                    />
                    <Route path="/transfer-money" element={<Transfer />} />
                    <Route path="/deposit-money" element={<Deposit />} />
                    <Route
                      path="/notifications"
                      element={<Notifications notii={noti} />}
                    />

                    <Route path="/settings" element={<Settings />} />
                    {/* <Route path="/faqs" element={<Faq />} /> */}

                    <Route path="/my-followers" element={<Followers />} />
                    <Route path="/qr-code" element={<QrCode />} />
                    <Route
                      path="/my-shop-center"
                      element={<MyShopCenter />}
                    />

                    <Route path="/blocked-user" element={<BlockedList />} />
                    <Route path="/favourite" element={<Favourite />} />
                    <Route path="/my-profile" element={<MyProfile />} />
                    <Route
                      path="/keep-me-posted-on"
                      element={<PostedOn />}
                    />
                    <Route
                      path="/purchase-request"
                      element={<PurchaseRequest />}
                    />

                    <Route
                      path="/manage-product/:id"
                      element={<ManageProduct />}
                    />
                    <Route
                      path="/bids-&-auctions"
                      element={<BidAuction />}
                    />
                    <Route
                      path="/select-address"
                      element={<SelectAddress />}
                    />
                    <Route path="/cart-buyer/:id" element={<CartBuyer />} />
                    <Route
                      path="/cart-seller/:id"
                      element={<CartSeller />}
                    />
                    <Route
                      path="/cart-request/:id"
                      element={<CartRequest />}
                    />

                    <Route
                      path="/bid-auction-request/:id"
                      element={<BidAuctionRequest />}
                    />

                    <Route
                      path="/cart-auction/:id"
                      element={<CartAuction />}
                    />
                    <Route path="/cart-bid/:id" element={<CartBid />} />
                    <Route
                      path="/cart-purchase-buyer/:id"
                      element={<CartPurBuyer />}
                    />
                    <Route
                      path="/cart-purchase-seller/:id"
                      element={<CartPurSeller />}
                    />
                    <Route
                      path="/create-shipment/:id"
                      element={<CreateShipment />}
                    />
                    <Route path="/edit-price/:id" element={<EditPrice />} />
                    <Route
                      path="/edit-auction/:id"
                      element={<EditAuction />}
                    />
                    <Route path="/edit-free/:id" element={<EditFree />} />
                    <Route
                      path="/qr-code-scanner"
                      element={<QrCodeScanner />}
                    />
                    <Route
                      path="/bid-payment/:id"
                      element={<BidPayment />}
                    />
                    <Route
                      path="/qr-code-payment/:id"
                      element={<QrCodePayment />}
                    />
                    <Route
                      path="/payment-status"
                      element={<PaymentSuccess />}
                    />
                    <Route
                      path="/seller-qr-code/:id"
                      element={<SellerQrCode />}
                    />
                    <Route path="/bank-details" element={<BankDetails />} />
                  </Route>
                  </Routes>
                </RouteErrorBoundary>
              </TranslationProvider>
            </Suspense>
          </BrowserRouter>
          </SocketProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}

export default React.memo(App);
